import React from 'react'
import { Hourglass } from 'lucide-react'
import { useBrand } from '@/hooks/useBrand'

export interface StlyMeta {
  monthsAvailable: number
  monthsTotal: number
  trackingStartedAt: string | null
  unlocksOn: string | null
}

interface Props {
  meta: StlyMeta
  propertyName: string
}

function formatDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

function formatMonthYear(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
  })
}

/**
 * Editorial advisory shown when STLY comparisons aren't yet meaningful
 * for this property — typically because we haven't been tracking it
 * for a full year. Calm amber tone, not alarming.
 *
 * The product name comes from `useBrand()` — never a literal (see lib/brand.ts).
 */
export function StlyAvailabilityBanner({ meta, propertyName }: Props) {
  const brand = useBrand()

  // Show only when STLY is genuinely unavailable (zero months) AND we know
  // when tracking began. Partial coverage (Holland Road's 9/12) is normal
  // and doesn't warrant a banner.
  if (meta.monthsAvailable > 0 || !meta.trackingStartedAt || !meta.unlocksOn) {
    return null
  }

  return (
    <aside
      role="note"
      className="relative overflow-hidden rounded-2xl border border-amber-200/70 bg-gradient-to-br from-amber-50/80 via-white to-amber-50/40 px-5 py-4 shadow-sm"
    >
      {/* decorative wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-amber-200/40 blur-3xl"
      />

      <div className="relative flex items-start gap-4">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 ring-1 ring-inset ring-amber-200">
          <Hourglass className="h-4 w-4 text-amber-700" strokeWidth={2.25} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-800">
            STLY Unavailable
          </p>
          <p className="mt-1 font-display text-base font-semibold tracking-tight text-foreground">
            Comparisons unlock in {formatMonthYear(meta.unlocksOn)}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {brand} started tracking{' '}
            <span className="font-medium text-foreground">{propertyName}</span> on{' '}
            <span className="font-medium text-foreground">{formatDate(meta.trackingStartedAt)}</span>.
            Point-in-time STLY needs at least one full year of booking history. Until then, use the{' '}
            <span className="font-medium text-foreground">Last year final</span> row on each card for
            a comparable signal.
          </p>
        </div>
      </div>
    </aside>
  )
}
